import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetOpdEntries } from "../../../../api/opd/opdEntryAPI";

import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { StoreSaleBillReturn } from "../../../../api/medicalStore/transaction/saleBillReturnAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

/* ================= VALIDATION ================= */
const schema = yup.object().shape({
  docType: yup.string().required(),
  creditNoteNo: yup.number().required(),
  date: yup.date().required(),
  time: yup.string().required(),
  customerType: yup.string().required(),
  opdEntryId: yup.string().nullable(),
  ipdEntryId: yup.string().nullable(),
  customerName: yup.string().required(),
  billNo: yup.number().required(),
  consultantId: yup.string().required(),
  departmentId: yup.string().required(),

  totalQty: yup.number().required(),
  grossAmount: yup.number().required(),
  gst: yup.number().required(),
  additionPercentage: yup.number().required(),
  additionAmount: yup.number().required(),
  discountPercentage: yup.number().required(),
  discountAmount: yup.number().required(),
  roundOff: yup.number().required(),
  netAmount: yup.number().required(),
  remark: yup.string().optional(),
  againstAccount: yup.string().optional(),

  medicineDetails: yup.array().of(
    yup.object().shape({
      categoryId: yup.string().required(),
      productId: yup.string().required(),
      expiryDate: yup.date().required(),
      batchNo: yup.number().required(),
      quantity: yup.number().required(),
      mrp: yup.number().required(),
      saleRate: yup.number().required(),
      amount: yup.number().required(),
      discountPercentage: yup.number().required(),
      discountAmount: yup.number().required(),
      gstPercentage: yup.number().required(),
      gstAmount: yup.number().required(),
      totalAmount: yup.number().required()
    })
  )
});

const AddSaleBillReturnEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [opdEntries, setOpdEntries] = useState([]);
  const [ipdEntries, setIpdEntries] = useState([]);
  const [consultants, setConsultants] = useState([]);

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      docType: "Sale Bill Entry",
      customerType: "OPD",
      medicineDetails: [
        {
          categoryId: "",
          productId: "",
          expiryDate: "",
          batchNo: "",
          quantity: "",
          mrp: "",
          saleRate: "",
          amount: "",
          discountPercentage: "",
          discountAmount: "",
          gstPercentage: "",
          gstAmount: "",
          totalAmount: ""
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "medicineDetails"
  });

  useEffect(() => {
    GetDepartments().then(r => r.success && setDepartments(r.data));
    GetCategoriesMedicalStore().then(r => r.success && setCategories(r.data));
    GetItems().then(r => r.success && setItems(r.data));
    GetOpdEntries().then(r => r.success && setOpdEntries(r.data));
    GetIpdEntries().then(r => r.success && setIpdEntries(r.data));
    GetConsultants().then(r => r.success && setConsultants(r.data));
  }, []);

  const calculateRow = index => {
    const qty = Number(getValues(`medicineDetails.${index}.quantity`) || 0);
    const rate = Number(getValues(`medicineDetails.${index}.saleRate`) || 0);
    const discPct = Number(
      getValues(`medicineDetails.${index}.discountPercentage`) || 0
    );
    const gstPct = Number(
      getValues(`medicineDetails.${index}.gstPercentage`) || 0
    );

    const amount = qty * rate;
    const discountAmount = (amount * discPct) / 100;
    const taxableAmount = amount - discountAmount;
    const gstAmount = (taxableAmount * gstPct) / 100;
    const totalAmount = taxableAmount + gstAmount;

    setValue(`medicineDetails.${index}.amount`, amount.toFixed(2));
    setValue(`medicineDetails.${index}.discountAmount`, discountAmount.toFixed(2));
    setValue(`medicineDetails.${index}.gstAmount`, gstAmount.toFixed(2));
    setValue(`medicineDetails.${index}.totalAmount`, totalAmount.toFixed(2));
  };

  const watched = useWatch({ control, name: "medicineDetails" });

  const roundOff = useWatch({
    control,
    name: "roundOff"
  });

  useEffect(() => {
    let totalQty = 0;
    let grossAmount = 0;
    let gst = 0;
    let discountAmount = 0;

    watched?.forEach(item => {
      totalQty += Number(item.quantity || 0);
      grossAmount += Number(item.amount || 0);
      gst += Number(item.gstAmount || 0);
      discountAmount += Number(item.discountAmount || 0);
    });

    const additionPct = Number(getValues("additionPercentage") || 0);
    const additionAmount = (grossAmount * additionPct) / 100;

    const discountPct = Number(getValues("discountPercentage") || 0);
    const discountAmt = (grossAmount * discountPct) / 100;

    const netAmount =
      grossAmount +
      gst +
      additionAmount -
      discountAmt;

    setValue("totalQty", totalQty);
    setValue("grossAmount", grossAmount.toFixed(2));
    setValue("gst", gst.toFixed(2));
    setValue("additionAmount", additionAmount.toFixed(2));
    setValue("discountAmount", discountAmt.toFixed(2));
    setValue("netAmount", netAmount.toFixed(Number(roundOff ||0)));

  }, [watched,roundOff, setValue, getValues]);


  const onSubmit = async data => {
    dispatch({ type: "LOADER_START" });
    try {
      const res = await StoreSaleBillReturn(data);
      res.success
        ? showToast("success", res.message)
        : showToast("error", res.message);

      if (res.success)
        navigate(
          `/medical-store/transaction/sale-bill-return-entry/list`
        );
    } catch (e) {
      showToast("error", e.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Add Sale Bill Return Entry" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add Sale Bill Return Entry</h5>
            <Link
              to={`/medical-store/transaction/sale-bill-return-entry/list`}
            >
              <button className="btn btn-primary" id="list">
                List Sale Bill Return
              </button>
              <UncontrolledTooltip target="list">
                Back to List
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
            <div className="card-body">

              {/* ================= HEADER ================= */}
              <div className="row">

                <div className="col-3">
                  <label>Document Type</label>
                  <input className="form-control" {...register("docType")} />
                  {errors.docType && <span className="text-danger">{errors.docType.message}</span>}
                </div>
                <div className="col-3">
                  <label>Credit Note No</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("creditNoteNo")}
                  />
                  {errors.creditNoteNo && (
                    <span className="text-danger">{errors.creditNoteNo.message}</span>
                  )}
                </div>

                <div className="col-3">
                  <label>Date</label>
                  <input type="date" className="form-control" {...register("date")} />
                  {errors.date && <span className="text-danger">{errors.date.message}</span>}
                </div>

                <div className="col-3">
                  <label>Time</label>
                  <input type="time" className="form-control" {...register("time")} />
                  {errors.time && <span className="text-danger">{errors.time.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Customer Type</label>
                  <select className="form-control" {...register("customerType")} >
                    <option value="">Select</option>
                    <option value="General">General</option>
                    <option value="Walkin">Walkin</option>
                    <option value="IPD">IPD</option>
                    <option value="OPD">OPD</option>
                  </select>
                  {errors.customerType && <span className="text-danger">{errors.customerType.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>OPD Entry No</label>
                  <select className="form-control" {...register("opdEntryId")} >
                    <option value="">Select</option>
                    {opdEntries.map(d => (
                      <option key={d._id} value={d._id}>{d.opdNo}</option>
                    ))}
                  </select>
                  {errors.opdEntryId && <span className="text-danger">{errors.opdEntryId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>IPD Entry No</label>
                  <select className="form-control" {...register("ipdEntryId")} >
                    <option value="">Select</option>
                    {ipdEntries.map(d => (
                      <option key={d._id} value={d._id}>{d.opdNo}</option>
                    ))}
                  </select>
                  {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Customer Name</label>
                  <input className="form-control" {...register("customerName")} />
                  {errors.customerName && <span className="text-danger">{errors.customerName.message}</span>}
                </div>

                <div className="col-3">
                  <label>Bill No</label>
                  <input type="number" step="any" className="form-control" {...register("billNo")} />
                  {errors.billNo && <span className="text-danger">{errors.billNo.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Consultant</label>
                  <select className="form-control" {...register("consultantId")} >
                    <option value="">Select</option>
                    {consultants.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                  {errors.consultantId && <span className="text-danger">{errors.consultantId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Department</label>
                  <select className="form-control" {...register("departmentId")}>
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                  {errors.departmentId && <span className="text-danger">{errors.departmentId.message}</span>}
                </div>

              </div>

              <hr />
              <h6>Medicine Details</h6>

              {fields.map((item, index) => (
                <div className="row border p-2 mb-2" key={item.id}>

                  <div className="col-2">
                    <label>Category</label>
                    <select className="form-control" {...register(`medicineDetails.${index}.categoryId`)}>
                      <option value="">Select</option>
                      {categories.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                    {errors.medicineDetails?.[index]?.categoryId &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].categoryId.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Product</label>
                    <select className="form-control" {...register(`medicineDetails.${index}.productId`)}>
                      <option value="">Select</option>
                      {items.map(p => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                    {errors.medicineDetails?.[index]?.productId &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].productId.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Expiry Date</label>
                    <input type="date" className="form-control"
                      {...register(`medicineDetails.${index}.expiryDate`)} />
                    {errors.medicineDetails?.[index]?.expiryDate &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].expiryDate.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Batch No</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.batchNo`)} />
                    {errors.medicineDetails?.[index]?.batchNo &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].batchNo.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Quantity</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.quantity`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.quantity &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].quantity.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>MRP</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.mrp`)} />
                    {errors.medicineDetails?.[index]?.mrp &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].mrp.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Sale Rate</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.saleRate`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.saleRate &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].saleRate.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.amount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>Discount %</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.discountPercentage`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.discountPercentage &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].discountPercentage.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Discount Amount</label>
                    <input className="form-control" readOnly
                      {...register(`medicineDetails.${index}.discountAmount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>GST %</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.gstPercentage`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.gstPercentage &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].gstPercentage.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>GST Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.gstAmount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>Total Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.totalAmount`)} />
                  </div>

                  <div className="col-1 mt-4">
                    <button type="button" className="btn btn-danger" onClick={() => remove(index)}>✕</button>
                  </div>

                </div>
              ))}

              <button type="button" className="btn btn-secondary" onClick={() => append({})}>
                + Add Medicine
              </button>

              <hr />

              {/* ================= SUMMARY ================= */}
              <div className="row">

                <div className="col-3">
                  <label>Total Qty</label>
                  <input type="number" step="any" className="form-control" {...register("totalQty")} />
                  {errors.totalQty && <span className="text-danger">{errors.totalQty.message}</span>}
                </div>

                <div className="col-3">
                  <label>Gross Amount</label>
                  <input type="number" step="any" className="form-control" {...register("grossAmount")} />
                  {errors.grossAmount && <span className="text-danger">{errors.grossAmount.message}</span>}
                </div>

                <div className="col-3">
                  <label>GST</label>
                  <input type="number" step="any" className="form-control" {...register("gst")} />
                  {errors.gst && <span className="text-danger">{errors.gst.message}</span>}
                </div>

                <div className="col-3">
                  <label>Addition %</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("additionPercentage")}
                  />
                  {errors.additionPercentage && <span className="text-danger">{errors.additionPercentage.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Addition Amount</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("additionAmount")}
                  />
                  {errors.additionAmount && <span className="text-danger">{errors.additionAmount.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Discount %</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("discountPercentage")}
                  />
                  {errors.discountPercentage && <span className="text-danger">{errors.discountPercentage.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Discount Amount</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("discountAmount")}
                  />
                  {errors.discountAmount && <span className="text-danger">{errors.discountAmount.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Round Off</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("roundOff")}
                  />
                  {errors.roundOff && <span className="text-danger">{errors.roundOff.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Net Amount</label>
                  <input
                    type="number" step="any"
                    className="form-control"
                    {...register("netAmount")}
                  />
                  {errors.netAmount && <span className="text-danger">{errors.netAmount.message}</span>}
                </div>

                <div className="col-6 mt-2">
                  <label>Remark</label>
                  <textarea className="form-control" {...register("remark")} />
                  {errors.remark && <span className="text-danger">{errors.remark.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Against Account</label>
                  <input className="form-control" {...register("againstAccount")} />
                  {errors.againstAccount && <span className="text-danger">{errors.againstAccount.message}</span>}
                </div>

              </div>

            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary" type="submit">
                Submit
              </button>
            </div>
          </form>


        </div>
      </div>
    </Fragment>
  );
};

export default AddSaleBillReturnEntry;
